import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import axios from "axios";
import { API_BASE_URL, PRIMARY } from "./shared";

// ─── OTPDialog ────────────────────────────────────────────────────────────────
const OTPDialog = ({
  open,
  onClose,
  mobile,
  storeId,
  onVerifySuccess,
  // Optional OTP use-case forwarded to send-otp. Defaults to the customer-
  // creation flow; pass "settlement" to authorise closing an existing
  // customer's loan (skips the "customer already exists" guard on resend).
  purpose,
}) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(30);
  const [isVerifying, setIsVerifying] = useState(false);
  const inputsRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const [snackBarConfig, setSnackBarConfig] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const closeSnackbar = () =>
    setSnackBarConfig((prev) => ({ ...prev, open: false }));

  useEffect(() => {
    if (open) {
      setOtp(["", "", "", "", "", ""]);
      setTimer(30);
      setIsVerifying(false);
      setTimeout(() => {
        if (inputsRef.current[0]) inputsRef.current[0].focus();
      }, 100);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    if (open && timer > 0 && !isVerifying) {
      timerIntervalRef.current = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [open, timer, isVerifying]);

  const handleChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 1);
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5 && inputsRef.current[index + 1]) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];
      if (otp[index]) {
        newOtp[index] = "";
      } else if (index > 0) {
        newOtp[index - 1] = "";
        inputsRef.current[index - 1]?.focus();
      }
      setOtp(newOtp);
    }
    if (e.key === "Enter") handleVerify();
  };

  const handleVerify = async () => {
    if (otp.some((d) => d === "")) {
      setSnackBarConfig({
        open: true,
        message: "Please enter full OTP",
        severity: "error",
      });
      return;
    }
    setIsVerifying(true);
    const payload = { mobile, otp: otp.join("") };
    // Pawn customer-master OTP verification — does NOT require the mobile to
    // belong to a registered Users record (unlike /api/auth/validateotp).
    const url = `${API_BASE_URL}/api/pawn/customers/verify-otp`;
    try {
      const res = await axios.post(url, payload);
      const { statusCode, statusMessage, token } = res?.data;
      if (statusCode === 200) {
        setSnackBarConfig({
          open: true,
          message: "OTP verified successfully!",
          severity: "success",
        });
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
        setTimeout(() => {
          onVerifySuccess(token);
          onClose();
        }, 1000);
      } else {
        setSnackBarConfig({
          open: true,
          message: statusMessage || "Invalid OTP. Try again",
          severity: "warning",
        });
        setOtp(["", "", "", "", "", ""]);
        setIsVerifying(false);
        setTimeout(() => {
          if (inputsRef.current[0]) inputsRef.current[0].focus();
        }, 100);
      }
    } catch (error) {
      setSnackBarConfig({
        open: true,
        message: error?.response?.data?.message || "OTP verification failed",
        severity: "error",
      });
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    setOtp(["", "", "", "", "", ""]);
    setTimer(30);
    setIsVerifying(false);
    setTimeout(() => {
      if (inputsRef.current[0]) inputsRef.current[0].focus();
    }, 100);
    const url = `${API_BASE_URL}/api/pawn/customers/send-otp`;
    try {
      const res = await axios.post(url, {
        mobile,
        store_id: storeId,
        ...(purpose ? { purpose } : {}),
      });
      const { success, statusCode, statusMessage } = res.data;
      if (!success) {
        setSnackBarConfig({
          open: true,
          message: statusMessage || "Failed to send OTP",
          severity: "warning",
        });
        return;
      }
      if (statusCode === 1) {
        setSnackBarConfig({
          open: true,
          message: "OTP sent successfully",
          severity: "success",
        });
      } else {
        setSnackBarConfig({
          open: true,
          message: statusMessage || "Failed to send OTP",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackBarConfig({
        open: true,
        message: "Failed to resend OTP",
        severity: "error",
      });
    }
  };

  const isOtpComplete = otp.every((digit) => digit !== "");

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <Typography variant="h5" fontWeight={600} sx={{ color: PRIMARY }}>
            Verify Mobile Number
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: "center", mb: 3 }}>
            Enter the 6-digit OTP sent to
            <br />
            <strong>{mobile}</strong>
          </DialogContentText>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 0.75, sm: 1.5 },
              mb: 2,
            }}
          >
            {otp.map((digit, index) => (
              <TextField
                key={index}
                inputRef={(el) => (inputsRef.current[index] = el)}
                value={digit}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 1,
                  style: {
                    textAlign: "center",
                    padding: "12px 0",
                  },
                }}
                sx={{
                  width: { xs: 34, sm: 46 },
                  "& input": { fontSize: { xs: 18, sm: 24 } },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: digit ? PRIMARY : "#e0e0e0",
                      borderWidth: digit ? 2 : 1,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: PRIMARY,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: PRIMARY,
                      borderWidth: 2,
                    },
                  },
                }}
                disabled={isVerifying}
              />
            ))}
          </Box>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography variant="caption" color="text.secondary">
              OTP validity:{" "}
              <span style={{ color: timer > 0 ? "#f44336" : "#4caf50" }}>
                {timer}s
              </span>
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">
                Didn't get the code?{" "}
                <span
                  style={{
                    color: timer === 0 ? PRIMARY : "gray",
                    textDecoration: timer === 0 ? "underline" : "none",
                    cursor: timer === 0 ? "pointer" : "not-allowed",
                    fontWeight: 600,
                  }}
                  onClick={
                    timer === 0 && !isVerifying ? handleResend : undefined
                  }
                >
                  Resend
                </span>
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            disabled={isVerifying}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleVerify}
            disabled={isVerifying || !isOtpComplete || timer === 0}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: PRIMARY,
              "&:hover": { backgroundColor: PRIMARY },
            }}
          >
            {isVerifying ? <CircularProgress size={24} /> : "Verify"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackBarConfig.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackBarConfig.severity}
          variant="filled"
        >
          {snackBarConfig.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OTPDialog;
